import { CAMPAIGN_ROOT_REGION_ID } from 'utils/constants/campaign';

import { TreeCollection } from '@hh.ru/magritte-ui';

// суффикс для элементов дерева "другое", чтобы отличать от других элементов дерева и понимать,
// к какому родителю относится этот элемент
export const OTHER_REGION_ID_SUFFIX = '_other';

export const isOtherRegionId = (id: string): boolean => id.endsWith(OTHER_REGION_ID_SUFFIX);

export const getOtherRegionId = (id: string): string => `${id}${OTHER_REGION_ID_SUFFIX}`;

/**
 * Функция для работы с исключениями регионов. Реализует функционал из CompositeSelection из блоко,
 * поскольку дерево в магритте не работает с исключениями.
 *
 * Если выбраны все регионы, то в стор сохраняем только корневой регион
 *
 * Если выбран корневой регион, а после исключены какие-либо регионы, в стор в качестве включенных
 * сохраняем корневой регион, в исключенные - не выбранные регионы. Здесь же проверяем вложенные исключения, например:
 * выбрали Россию, исключили город Химки в Московской области - будет выбрана Россия и Московская область и исключены Химки
 *
 * @param selected - выбранные регионы
 * @param excluded - исключенные регионы
 * @param collection - коллекция регионов
 * @returns {includedRegions: string[], excludedRegions: string[]}
 */
export const getIncludedAndExcludedRegionsIds = (
    selected: string[],
    excluded: string[],
    collection: TreeCollection
): {
    includedRegions: string[];
    excludedRegions: string[];
} => {
    const includedSet = new Set<string>();
    const excludedSet = new Set<string>();

    const hasAncestorIncluded = (id: string): boolean => {
        const parentId = collection.getParentId(id);
        if (!parentId) {
            return false;
        }
        if (includedSet.has(parentId)) {
            return true;
        }
        return hasAncestorIncluded(parentId);
    };

    const hasAncestorInSelected = (id: string): boolean => {
        const parentId = collection.getParentId(id);
        if (!parentId) {
            return false;
        }
        if (selected.includes(parentId)) {
            return true;
        }
        return hasAncestorInSelected(parentId);
    };

    excluded.forEach((id) => {
        const parentId = collection.getParentIds(id);
        if (!selected.includes(id) && parentId.some((id) => selected.includes(id))) {
            excludedSet.add(id);
        }
    });

    selected.forEach((id) => {
        if (id === CAMPAIGN_ROOT_REGION_ID) {
            includedSet.add(id);

            const childrenIds = collection.getChildrenIds(id);
            for (const childId of childrenIds) {
                includedSet.delete(childId);
            }
            // исключаем невыбранные регионы
            for (const childId of childrenIds) {
                if (
                    !selected.includes(childId) &&
                    !selected.includes(getOtherRegionId(childId)) &&
                    childrenIds.some((childId) => selected.includes(childId))
                ) {
                    excludedSet.add(childId);
                }
            }
        } else if (collection.hasChildren(id)) {
            if (!hasAncestorInSelected(id)) {
                includedSet.add(id);
                const childrenIds = collection.getChildrenIds(id);
                for (const childId of childrenIds) {
                    includedSet.delete(childId);
                }
            }
        } else if (isOtherRegionId(id)) {
            // Если id содержит суффикс '_other' - ищем родителя
            const parentId = id.slice(0, -OTHER_REGION_ID_SUFFIX.length);
            if (
                collection.hasChildren(parentId) &&
                !hasAncestorInSelected(parentId) &&
                !hasAncestorIncluded(parentId)
            ) {
                includedSet.add(parentId);
            }
            const childrenIds = collection.getChildrenIds(parentId);
            for (const childId of childrenIds) {
                includedSet.delete(childId);
            }
            for (const childId of childrenIds) {
                if (!selected.includes(childId)) {
                    excludedSet.add(childId);
                }
            }
        } else if (
            !collection.hasChildren(id) &&
            ((!hasAncestorInSelected(id) && !hasAncestorIncluded(id)) ||
                !selected.includes(collection.getParentId(id) ?? ''))
        ) {
            includedSet.add(id);
        }
    });

    const includedRegions = [...includedSet];
    const excludedRegions = [...excludedSet];

    return {
        includedRegions,
        excludedRegions,
    };
};
